import React, { useState, useEffect } from 'react';

interface User {
  id: string;
  full_name: string;
  email: string;
  role: string;
  status: 'active' | 'inactive' | 'pending';
  created_at: string;
  last_login?: string;
}

interface UserFormProps {
  user?: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (userData: Partial<User>) => void;
  loading?: boolean;
  currentUser?: any;
}

interface FormData {
  full_name: string;
  email: string;
  role: string;
  status: 'active' | 'inactive' | 'pending';
  password?: string;
  confirmPassword?: string;
}

interface FormErrors {
  full_name?: string;
  email?: string;
  role?: string;
  password?: string;
  confirmPassword?: string;
}

export default function UserForm({ user, isOpen, onClose, onSubmit, loading = false, currentUser }: UserFormProps) {
  const [formData, setFormData] = useState<FormData>({
    full_name: '',
    email: '',
    role: 'Viewer',
    status: 'pending'
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isEditMode, setIsEditMode] = useState(false);

  // RBAC helper functions
  const isSuperAdmin = () => currentUser?.role === 'Super Admin';
  const isAdmin = () => currentUser?.role === 'Admin' || isSuperAdmin();
  const canAssignSuperAdminRole = () => isSuperAdmin();
  const canAssignAdminRole = () => isAdmin();
  const canEditRole = (targetUser: User | null | undefined) => {
    if (!targetUser) return true; // Creating new user
    if (isSuperAdmin()) return true;
    return targetUser.role !== 'Super Admin';
  };
  const canEditStatus = (targetUser: User | null | undefined) => {
    if (!targetUser) return true; // Creating new user
    if (isSuperAdmin()) return true;
    return targetUser.role !== 'Super Admin';
  };

  const roles = [
    { value: 'Super Admin', label: 'Super Admin', description: 'Full system access', disabled: !canAssignSuperAdminRole() },
    { value: 'Admin', label: 'Admin', description: 'Manage users and content', disabled: !canAssignAdminRole() },
    { value: 'Editor', label: 'Editor', description: 'Edit content and moderate', disabled: false },
    { value: 'Viewer', label: 'Viewer', description: 'View-only access', disabled: false }
  ];

  const statuses = [
    { value: 'active', label: 'Active', description: 'User can access the system' },
    { value: 'inactive', label: 'Inactive', description: 'User account is disabled' },
    { value: 'pending', label: 'Pending', description: 'Awaiting activation' }
  ];

  useEffect(() => {
    if (user) {
      setIsEditMode(true);
      setFormData({
        full_name: user.full_name,
        email: user.email,
        role: user.role,
        status: user.status
      });
    } else {
      setIsEditMode(false);
      setFormData({
        full_name: '',
        email: '',
        role: 'Viewer',
        status: 'pending'
      });
    }
    setErrors({});
  }, [user, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Full name validation
    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    } else if (formData.full_name.trim().length < 2) {
      newErrors.full_name = 'Full name must be at least 2 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Role validation
    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    // Password validation for new users
    if (!isEditMode) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
        newErrors.password = 'Password must contain uppercase, lowercase, and number';
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      const submitData: Partial<User> = {
        full_name: formData.full_name.trim(),
        email: formData.email.trim(),
        role: formData.role,
        status: formData.status
      };

      if (!isEditMode && formData.password) {
        (submitData as any).password = formData.password;
      }

      onSubmit(submitData);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditMode ? 'Edit User' : 'Create New User'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => handleInputChange('full_name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.full_name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter full name"
              />
              {errors.full_name && (
                <p className="text-red-500 text-sm mt-1">{errors.full_name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter email address"
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role *
              </label>
              <select
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                disabled={!canEditRole(user) || loading}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.role ? 'border-red-500' : 'border-gray-300'
                } ${!canEditRole(user) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              >
                {roles.map((role) => (
                  <option key={role.value} value={role.value} disabled={role.disabled}>
                    {role.label} - {role.description}
                  </option>
                ))}
              </select>
              {errors.role && (
                <p className="text-red-500 text-sm mt-1">{errors.role}</p>
              )}
              {!canEditRole(user) && (
                <p className="text-gray-500 text-sm mt-1">
                  Only Super Admins can modify Super Admin roles
                </p>
              )}
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value as any)}
                disabled={!canEditStatus(user) || loading}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  !canEditStatus(user) ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
                }`}
              >
                {statuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label} - {status.description}
                  </option>
                ))}
              </select>
              {!canEditStatus(user) && (
                <p className="text-gray-500 text-sm mt-1">
                  Only Super Admins can modify Super Admin status
                </p>
              )}
            </div>

            {/* Password fields for new users only */}
            {!isEditMode && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password *
                  </label>
                  <input
                    type="password"
                    value={formData.password || ''}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter password"
                  />
                  {errors.password && (
                    <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    value={formData.confirmPassword || ''}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Confirm password"
                  />
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
                  )}
                </div>
              </>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isEditMode ? 'Updating...' : 'Creating...'}
                  </div>
                ) : (
                  isEditMode ? 'Update User' : 'Create User'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 